import { createStore, del, get, set } from 'idb-keyval'

/**
 * Creates a persistent storage mechanism using IndexedDB
 * Safely handles different environments and provides type-safe storage
 */
const createWorkspaceStorage = (dbName = 'workspace2-db', storeName = 'workspace2-store') => {
    // Ensure IndexedDB is available before creating store
    if (typeof indexedDB === 'undefined') {
        console.warn(
            'IndexedDB is not supported. Falling back to in-memory storage.'
        )
        const inMemoryStore: Record<string, any> = {}

        return {
            getItem: async (name: string) => inMemoryStore[name] || null,
            setItem: async (name: string, value: any) => {
                inMemoryStore[name] = value
            },
            removeItem: async (name: string) => {
                delete inMemoryStore[name]
            },
        }
    }

    const workspaceDB = createStore(dbName, storeName)

    return {
        /**
         * Retrieves an item from indexed DB
         * @param name - The key to retrieve
         * @returns The stored value or null if not found
         */
        getItem: async <T = unknown>(name: string): Promise<T | null> => {
            try {
                return (await get(name, workspaceDB)) || null
            } catch (error) {
                console.error('Error retrieving item from IndexedDB:', error)
                return null
            }
        },

        /**
         * Stores an item in indexed DB
         * @param name - The key to store
         * @param value - The value to store
         */
        setItem: async (name: string, value: unknown): Promise<void> => {
            try {
                await set(name, value, workspaceDB)
            } catch (error) {
                console.error('Error storing item in IndexedDB:', error)
            }
        },

        /**
         * Removes an item from indexed DB
         * @param name - The key to remove
         */
        removeItem: async (name: string): Promise<void> => {
            try {
                await del(name, workspaceDB)
            } catch (error) {
                console.error('Error removing item from IndexedDB:', error)
            }
        },
    }
}

export const indexedDBStorage = createWorkspaceStorage()
